<template>
    <div ref="viewport" class="virtualized-tree-list" role="list" @scroll.passive="handleScroll">
        <div class="virtualized-tree-list__spacer" :style="{ height: `${totalHeight}px` }">
            <div v-for="row in visibleRows" :key="row.key" class="virtualized-tree-list__row" role="listitem"
                :data-virtual-key="row.key" :style="{ transform: `translateY(${row.offset}px)` }">
                <slot :item="row.item" :index="row.index" />
            </div>
        </div>
    </div>
</template>

<script>
import { buildVirtualOffsets, getVirtualRange } from '@/utils/treePerformance.mjs';

export default {
    name: 'VirtualizedTreeList',
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        itemKey: {
            type: [String, Function],
            default: 'id',
        },
        estimatedRowHeight: {
            type: Number,
            default: 40,
        },
        overscan: {
            type: Number,
            default: 8,
        },
        initialScrollOffset: {
            type: Number,
            default: 0,
        },
    },
    emits: ['scroll-offset'],
    data() {
        return {
            scrollOffset: 0,
            viewportHeight: 0,
            offsets: [0],
            measuredHeights: new Map(),
            observedRows: new Set(),
            pendingMeasurements: new Map(),
            measurementRaf: null,
            viewportObserver: null,
            rowObserver: null,
            initialOffsetApplied: false,
        };
    },
    computed: {
        totalHeight() {
            return this.offsets[this.offsets.length - 1] || 0;
        },
        visibleRange() {
            return getVirtualRange({
                offsets: this.offsets,
                scrollOffset: this.scrollOffset,
                viewportHeight: this.viewportHeight,
                overscan: this.overscan,
            });
        },
        visibleRows() {
            const rows = [];
            for (let index = this.visibleRange.start; index < this.visibleRange.end; index += 1) {
                const item = this.items[index];
                rows.push({
                    item,
                    index,
                    key: this.resolveKey(item, index),
                    offset: this.offsets[index] || 0,
                });
            }
            return rows;
        },
    },
    watch: {
        items: {
            immediate: true,
            handler() {
                this.rebuildOffsets();
                this.$nextTick(() => {
                    this.applyInitialOffset();
                    this.syncObservedRows();
                });
            },
        },
        initialScrollOffset(value) {
            if (!this.initialOffsetApplied) return;
            this.setScrollOffset(value, false);
        },
    },
    mounted() {
        this.viewportHeight = this.$refs.viewport?.clientHeight || 0;
        if (typeof ResizeObserver !== 'undefined') {
            this.viewportObserver = new ResizeObserver((entries) => {
                const entry = entries[0];
                this.viewportHeight = entry?.contentRect?.height || this.$refs.viewport?.clientHeight || 0;
            });
            this.viewportObserver.observe(this.$refs.viewport);

            this.rowObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const key = entry.target?.dataset?.virtualKey;
                    if (!key) continue;
                    const height = entry.borderBoxSize?.[0]?.blockSize || entry.contentRect?.height;
                    if (Number.isFinite(height) && height > 0) this.pendingMeasurements.set(key, height);
                }
                this.scheduleMeasurementFlush();
            });
        }
        this.applyInitialOffset();
        this.syncObservedRows();
    },
    updated() {
        this.syncObservedRows();
    },
    beforeUnmount() {
        if (this.measurementRaf) cancelAnimationFrame(this.measurementRaf);
        this.measurementRaf = null;
        this.viewportObserver?.disconnect();
        this.rowObserver?.disconnect();
        this.observedRows.clear();
        this.pendingMeasurements.clear();
    },
    methods: {
        resolveKey(item, index) {
            if (typeof this.itemKey === 'function') return String(this.itemKey(item, index));
            if (item && item[this.itemKey] != null) return String(item[this.itemKey]);
            return String(index);
        },
        rebuildOffsets() {
            this.offsets = buildVirtualOffsets(
                this.items,
                this.itemKey,
                this.measuredHeights,
                this.estimatedRowHeight,
            );
        },
        applyInitialOffset() {
            if (this.initialOffsetApplied || !this.$refs.viewport || !this.items.length) return;
            this.initialOffsetApplied = true;
            this.setScrollOffset(this.initialScrollOffset, false);
        },
        handleScroll(event) {
            this.scrollOffset = event.currentTarget.scrollTop;
            this.$emit('scroll-offset', this.scrollOffset);
        },
        setScrollOffset(offset, emit = true) {
            const viewport = this.$refs.viewport;
            if (!viewport) return;
            const maximum = Math.max(0, this.totalHeight - viewport.clientHeight);
            const nextOffset = Math.min(maximum, Math.max(0, Number(offset) || 0));
            viewport.scrollTop = nextOffset;
            this.scrollOffset = nextOffset;
            if (emit) this.$emit('scroll-offset', nextOffset);
        },
        scrollToKey(key, align = 'center') {
            const target = String(key);
            const index = this.items.findIndex((item, itemIndex) => this.resolveKey(item, itemIndex) === target);
            if (index < 0) return false;
            const start = this.offsets[index] || 0;
            const end = this.offsets[index + 1] || start + this.estimatedRowHeight;
            const viewportHeight = this.$refs.viewport?.clientHeight || this.viewportHeight || 0;
            let offset = start;
            if (align === 'center') offset = start - (viewportHeight - (end - start)) / 2;
            else if (align === 'end') offset = end - viewportHeight;
            else if (align === 'auto') {
                if (start >= this.scrollOffset && end <= this.scrollOffset + viewportHeight) return true;
                offset = start < this.scrollOffset ? start : end - viewportHeight;
            }
            this.setScrollOffset(offset);
            return true;
        },
        refreshMeasurements() {
            this.measuredHeights.clear();
            this.rebuildOffsets();
            this.$nextTick(this.syncObservedRows);
        },
        syncObservedRows() {
            if (!this.rowObserver || !this.$refs.viewport) return;
            const currentRows = new Set(this.$refs.viewport.querySelectorAll('.virtualized-tree-list__row'));
            for (const row of this.observedRows) {
                if (!currentRows.has(row)) {
                    this.rowObserver.unobserve(row);
                    this.observedRows.delete(row);
                }
            }
            for (const row of currentRows) {
                if (this.observedRows.has(row)) continue;
                this.observedRows.add(row);
                this.rowObserver.observe(row);
            }
        },
        scheduleMeasurementFlush() {
            if (this.measurementRaf) return;
            this.measurementRaf = requestAnimationFrame(() => {
                this.measurementRaf = null;
                let changed = false;
                for (const [key, height] of this.pendingMeasurements) {
                    if (Math.abs((this.measuredHeights.get(key) || 0) - height) < 0.5) continue;
                    this.measuredHeights.set(key, height);
                    changed = true;
                }
                this.pendingMeasurements.clear();
                if (changed) this.rebuildOffsets();
            });
        },
    },
};
</script>

<style scoped>
.virtualized-tree-list {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
}

.virtualized-tree-list__spacer {
    position: relative;
    width: 100%;
}

.virtualized-tree-list__row {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    contain: layout style;
}
</style>
